import { defineDocument } from '../../schema';
export const blogScrubIntoSecondOpinionsDocument = defineDocument({
    id: 'blog:scrub-into-second-opinions',
    kind: 'blog',
    slug: 'scrub-into-second-opinions',
    route: '/blog/scrub-into-second-opinions',
    seo: {
        title: 'Scrub Into Second Opinions',
        description: 'A game of deception where Good Doctors heal and Bad Doctors secretly sabotage',
        imageAsset: 'coverSecondOpinions',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Scrub Into Second Opinions',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A game of deception where Good Doctors heal and Bad Doctors secretly sabotage',
            },
        ],
        imageAsset: 'coverSecondOpinions',
        imageAlt: 'Second Opinions cover art',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2025-12-04T00:00:00.000Z'),
        tags: ['games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy is on the table and the monitors are flatlining. Whether Poppy makes it out depends entirely on which of you can be trusted to hold the scalpel.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'link',
                            href: '/discover/games/second-opinions',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Second Opinions',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: " joins our suite of party games today, pitting Good Doctors against Bad Doctors in a high-stakes battle for Poppy's life. Every round a new Chief of Medicine picks a committee that will decide on a treatment to either stabilize Poppy's vitals or sabotage the recovery. Docs accuse each other of malpractice... and might find their own medical licenses revoked in the process.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Good Doctors win by fully healing Poppy or eliminating all Bad Doctors. If Poppy flatlines, Bad Doctors win and we all lose a bit of faith in the medical system.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__the-art-of-medical-malpractice',
                    title: [
                        {
                            type: 'text',
                            text: 'The Art of Medical Malpractice',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Second Opinions was inspired by games like Secret Hitler, Werewolf/Mafia, and Among Us: games of deception and strategy where hidden roles create an environment of suspicion and betrayal.',
                                },
                            ],
                        },
                        {
                            type: 'quote',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The secret doctor dynamic worked well for creating tension between "hurting" and "healing" the patient. It really clicked when I realized malpractice was a great guise for elimination.',
                                },
                            ],
                            attribution: [
                                {
                                    type: 'text',
                                    text: 'Jay, Developer',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "The operating table became the perfect setting for a game with high-stakes elimination riding on every vote. The medical theme naturally creates some conflict: every treatment decision matters and every accusation could mean someone gets voted out of the operating room. It's a highly strategic game, but I love that a little luck is involved too. You never know which cards the Chief will have to choose from.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__designing-remote-deception',
                    title: [
                        {
                            type: 'text',
                            text: 'Designing Remote Deception',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Translating this hospital drama into a digital space was its own surgical challenge. We wanted the tension of an in-person accusation to translate perfectly to your screen because our games are often played remotely by streams and teams. We designed the voting mechanic to mimic how players might discuss and accuse each other in-person, showing everyone's vote above their avatar and also allowing players to change their vote if they are convinced to do so before the time is up.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "This game was a ton of fun to develop. It has one of the most evolved game themes, it's hilarious to see what treatments are applied, and the soundtrack is one of the catchiest we've crafted yet.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__the-medical-drama',
                    title: [
                        {
                            type: 'text',
                            text: 'The Medical Drama',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'This was a game I had been thinking about for a long time. I needed to make it, even though I knew it would not be for everyone. It has been enjoyed most by big-brain strategy players and also in-person groups who spend the voting period shouting accusations, defenses, and deflections at each other.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "At first glance, it might seem like the Good Doctors have the upper hand. However, the 'Bad Doctors' hold a few hidden aces: the deck is actually weighted with 50% more HURT cards, and their total awareness of everyone's roles gives them a massive information advantage from day one.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__the-doctor-s-perspective',
                    title: [
                        {
                            type: 'text',
                            text: "The Doctor's Perspective",
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "The top-down view of the operating table with doctors surrounding it is a subtle detail I don't know if all players will appreciate. It visually reinforces the feeling of the patient's health being the central focus and it creates tension as players debate who to trust and who to vote out.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__scrub-in-trust-no-one',
                    title: [
                        {
                            type: 'text',
                            text: 'Scrub In, Trust No One',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'See if you have what it takes to save a life—or end one. Share your wildest medical moments in ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Pals Discord",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Operate on Second Opinions now.',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' My life is in your wildly unqualified hands.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=scrub-into-second-opinions.js.map